import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X, Tv } from 'lucide-react';

/**
 * StreamingModal - Modal component to display live streaming video
 * Supports YouTube, Twitch, and generic streaming URLs
 */
export default function StreamingModal({ open, onOpenChange, streamingUrl, eventName }) {
    const [error, setError] = useState(false);

    // Convert streaming URL to embed format (memoized to avoid re-render loops)
    const { embedUrl, urlError } = useMemo(() => {
        if (!streamingUrl) return { embedUrl: '', urlError: false };

        try {
            const urlObj = new URL(streamingUrl);

            // YouTube
            if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
                let videoId;
                if (urlObj.hostname.includes('youtu.be')) {
                    videoId = urlObj.pathname.slice(1);
                } else {
                    videoId = urlObj.searchParams.get('v');
                }
                return { embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`, urlError: false };
            }

            // Twitch
            if (urlObj.hostname.includes('twitch.tv')) {
                const channel = urlObj.pathname.slice(1);
                return { embedUrl: `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}&autoplay=true`, urlError: false };
            }

            // Generic - use as-is
            return { embedUrl: streamingUrl, urlError: false };
        } catch (e) {
            return { embedUrl: '', urlError: true };
        }
    }, [streamingUrl]);

    const hasError = error || urlError;

    if (!streamingUrl) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl w-full p-0" data-testid="streaming-modal">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Tv className="w-5 h-5 text-primary" />
                            <DialogTitle className="font-heading text-lg">
                                {eventName ? `Streaming: ${eventName}` : 'Streaming en Vivo'}
                            </DialogTitle>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                            className="h-8 w-8"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </DialogHeader>

                <div className="relative" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
                    {hasError ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted">
                            <div className="text-center p-6">
                                <Tv className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground mb-2">Error al cargar el streaming</p>
                                <p className="text-sm text-muted-foreground/70">
                                    Verifica que la URL sea correcta
                                </p>
                                <a
                                    href={streamingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-sm hover:underline mt-2 inline-block"
                                >
                                    Abrir en nueva pestaña
                                </a>
                            </div>
                        </div>
                    ) : (
                        <iframe
                            src={embedUrl}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Live Streaming"
                            onError={() => setError(true)}
                        />
                    )}
                </div>

                {!hasError && (
                    <div className="px-6 py-4 bg-muted/30 text-sm text-muted-foreground flex items-center justify-between">
                        <span>Streaming en vivo</span>
                        <a
                            href={streamingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Abrir en nueva pestaña
                        </a>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
